import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface MarketData {
  average: number;
  percentiles: {
    p10: number;
    p25: number;
    p50: number;
    p75: number;
    p90: number;
  };
}

interface SalaryRangeChartProps {
  marketData: MarketData;
  currentSalary: number | null;
  jobTitle: string;
}

export function SalaryRangeChart({ marketData, currentSalary, jobTitle }: SalaryRangeChartProps) {
  const min = marketData.percentiles.p10;
  const max = marketData.percentiles.p90;
  const range = max - min;

  // Calculate positions as percentages
  const getPosition = (value: number) => {
    return ((value - min) / range) * 100;
  };

  const markers = [
    { value: marketData.percentiles.p10, label: "10th", color: "bg-red-500" },
    { value: marketData.percentiles.p25, label: "25th", color: "bg-orange-500" },
    { value: marketData.percentiles.p50, label: "50th", color: "bg-yellow-500" },
    { value: marketData.percentiles.p75, label: "75th", color: "bg-green-500" },
    { value: marketData.percentiles.p90, label: "90th", color: "bg-blue-500" },
  ];

  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div>
          <h3 className="font-semibold mb-2">Salary Distribution for {jobTitle}</h3>
          <p className="text-sm text-muted-foreground mb-6">
            See where different salary levels fall in the market
          </p>
        </div>

        {/* Chart Container */}
        <div className="relative">
          {/* Scale labels */}
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>${(min / 1000).toFixed(0)}k</span>
            <span>${(max / 1000).toFixed(0)}k</span>
          </div>

          {/* Main bar */}
          <div className="relative h-12 bg-gradient-to-r from-red-100 via-yellow-100 via-green-100 to-blue-100 dark:from-red-950 dark:via-yellow-950 dark:via-green-950 dark:to-blue-950 rounded-full overflow-hidden">
            {/* Percentile markers */}
            {markers.map((marker) => (
              <div
                key={marker.label}
                className="absolute top-0 h-full w-1 bg-background"
                style={{ left: `${getPosition(marker.value)}%` }}
              />
            ))}

            {/* Current salary indicator */}
            {currentSalary && currentSalary >= min && currentSalary <= max && (
              <div
                className="absolute top-0 h-full flex items-center"
                style={{ left: `${getPosition(currentSalary)}%` }}
              >
                <div className="relative">
                  <div className="absolute -top-8 -left-12 w-24 text-center">
                    <Badge variant="default" className="whitespace-nowrap">
                      You: ${(currentSalary / 1000).toFixed(0)}k
                    </Badge>
                  </div>
                  <div className="w-1 h-12 bg-primary animate-pulse" />
                  <div className="absolute -bottom-2 -left-2 w-5 h-5 rounded-full bg-primary border-2 border-background" />
                </div>
              </div>
            )}
          </div>

          {/* Percentile labels below */}
          <div className="relative mt-4">
            {markers.map((marker) => (
              <div
                key={marker.label}
                className="absolute -ml-8 text-center"
                style={{ left: `${getPosition(marker.value)}%` }}
              >
                <div className="text-xs font-medium">{marker.label}</div>
                <div className="text-xs text-muted-foreground">
                  ${(marker.value / 1000).toFixed(0)}k
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 justify-center pt-8 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Below Market</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>Market Average</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Above Market</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Top Earners</span>
          </div>
        </div>

        {/* Insights */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <h4 className="font-semibold text-sm">What This Means</h4>
          <ul className="text-sm space-y-1 text-muted-foreground">
            <li>• <strong>10th-25th percentile:</strong> Entry-level or below-market compensation</li>
            <li>• <strong>25th-50th percentile:</strong> Fair market value for early career</li>
            <li>• <strong>50th-75th percentile:</strong> Competitive compensation</li>
            <li>• <strong>75th-90th percentile:</strong> Above-average to excellent compensation</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
